import { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { useSkills } from '@/hooks/use-content';
import type { SkillRow as Skill } from '@/types/database';
import SectionEyebrow from './SectionEyebrow';

const SkillCard = memo(({ skill }: { skill: Skill }) => {
  return (
    <div className="group flex flex-col items-center gap-2 sm:gap-3 p-3 sm:p-6 rounded-xl bg-card hover:shadow-card transition-all duration-300 hover:-translate-y-1">
      <div className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-white/95 p-2 sm:p-2.5">
        <div className="absolute inset-0 bg-gradient-primary rounded-lg opacity-0 group-hover:opacity-10 transition-opacity"></div>
        <img
          src={skill.logo_url}
          alt={skill.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain relative z-10"
          onError={(e) => {
            e.currentTarget.src = "https://via.placeholder.com/64?text=" + skill.name.charAt(0);
          }}
        />
      </div>
      <span className="text-xs sm:text-sm font-medium text-center">{skill.name}</span>
    </div>
  );
});
SkillCard.displayName = 'SkillCard';

const Skills = () => {
  const { t } = useTranslation();
  const { data: skillsData, isLoading } = useSkills();

  return (
    <section id="skills" className="py-14 sm:py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <SectionEyebrow index="02" label={t('skills.eyebrow')} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('skills.title')}</h2>
          <p className="text-muted-foreground text-lg">
            <Trans i18nKey="skills.description" components={{ b: <strong /> }} />
          </p>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 max-w-5xl mx-auto">
          {isLoading
            ? Array.from({ length: 12 }).map((_, idx) => <Skeleton key={idx} className="h-28 sm:h-36 rounded-xl" />)
            : skillsData?.map((skill) => <SkillCard key={skill.id} skill={skill} />)}
        </div>
      </div>
    </section>
  );
};

export default Skills;
